import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { CalendarDays, ChevronLeft, ChevronRight } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";

interface DateTimePickerProps {
  value: string | null;
  onChange: (value: string | null) => void;
  disabled?: boolean;
  placeholder?: string;
  className?: string;
}

const MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
const WEEKDAYS = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"];

function isSameDay(left: Date, right: Date) {
  return left.getFullYear() === right.getFullYear() && left.getMonth() === right.getMonth() && left.getDate() === right.getDate();
}

function dateFromIso(value: string | null) {
  if (!value) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

/** Mouse-driven local calendar and time picker that stores UTC ISO strings. */
export function DateTimePicker({ value, onChange, disabled, placeholder = "Choose date and time", className }: DateTimePickerProps) {
  const selected = dateFromIso(value);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const dialogRef = useRef<HTMLDialogElement>(null);
  const [open, setOpen] = useState(false);
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const [displayedMonth, setDisplayedMonth] = useState(() => {
    const initial = selected ?? new Date();
    return new Date(initial.getFullYear(), initial.getMonth(), 1);
  });
  const days = useMemo(() => {
    const offset = displayedMonth.getDay();
    const count = new Date(displayedMonth.getFullYear(), displayedMonth.getMonth() + 1, 0).getDate();
    return Array.from({ length: offset + count }, (_, index) => index < offset ? null : index - offset + 1);
  }, [displayedMonth]);

  function chooseDay(day: number) {
    const current = selected ?? new Date();
    onChange(new Date(displayedMonth.getFullYear(), displayedMonth.getMonth(), day, current.getHours(), current.getMinutes(), 0, 0).toISOString());
  }
  function changeTime(part: "hour" | "minute", raw: string) {
    const next = new Date(selected ?? new Date());
    if (part === "hour") next.setHours(Number(raw)); else next.setMinutes(Number(raw));
    next.setSeconds(0, 0);
    onChange(next.toISOString());
  }
  function toggle() {
    if (!open) { const current = selected ?? new Date(); setDisplayedMonth(new Date(current.getFullYear(), current.getMonth(), 1)); }
    setOpen((current) => !current);
  }

  useEffect(() => {
    if (!open) return;
    const updatePosition = () => {
      const rect = triggerRef.current?.getBoundingClientRect();
      if (!rect) return;
      const width = 320;
      const height = 350;
      const left = Math.max(8, Math.min(rect.left, window.innerWidth - width - 8));
      const top = rect.bottom + 8 + height <= window.innerHeight ? rect.bottom + 8 : Math.max(8, rect.top - height - 8);
      setPosition({ top, left });
    };
    updatePosition();
    window.addEventListener("resize", updatePosition);
    window.addEventListener("scroll", updatePosition, true);
    return () => { window.removeEventListener("resize", updatePosition); window.removeEventListener("scroll", updatePosition, true); };
  }, [open]);

  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;
    if (open && !dialog.open) dialog.showModal();
    if (!open && dialog.open) dialog.close();
  }, [open]);

  return <div className={cn("relative", className)}>
    <Button ref={triggerRef} type="button" variant="outline" className="w-full justify-between font-normal" onClick={toggle} disabled={disabled} aria-haspopup="dialog" aria-expanded={open}>
      <span className={cn("truncate", !selected && "text-muted-foreground")}>{selected ? selected.toLocaleString() : placeholder}</span><CalendarDays className="h-4 w-4 shrink-0" />
    </Button>
    {open && createPortal(<dialog ref={dialogRef} onClose={() => setOpen(false)} aria-label="Choose date and time" className="fixed z-[9999] m-0 w-80 rounded-lg border border-zinc-700 !bg-[#121217] !text-zinc-100 p-3 shadow-2xl backdrop:bg-black/55" style={position}>
      <div className="mb-3 flex items-center justify-between"><Button type="button" size="icon" variant="ghost" className="h-7 w-7 text-zinc-100 hover:bg-zinc-800" onClick={() => setDisplayedMonth((month) => new Date(month.getFullYear(), month.getMonth() - 1, 1))} aria-label="Previous month"><ChevronLeft className="h-4 w-4" /></Button><span className="text-sm font-semibold text-white">{MONTHS[displayedMonth.getMonth()]} {displayedMonth.getFullYear()}</span><Button type="button" size="icon" variant="ghost" className="h-7 w-7 text-zinc-100 hover:bg-zinc-800" onClick={() => setDisplayedMonth((month) => new Date(month.getFullYear(), month.getMonth() + 1, 1))} aria-label="Next month"><ChevronRight className="h-4 w-4" /></Button></div>
      <div className="grid grid-cols-7 gap-1 text-center text-xs text-zinc-400">{WEEKDAYS.map((day) => <span key={day}>{day}</span>)}</div>
      <div className="mt-1 grid grid-cols-7 gap-1">{days.map((day, index) => day === null ? <span key={`blank-${index}`} /> : <button key={day} type="button" onClick={() => chooseDay(day)} className={cn("h-8 rounded-md text-sm text-zinc-100 hover:bg-zinc-800", selected && selected.getFullYear() === displayedMonth.getFullYear() && selected.getMonth() === displayedMonth.getMonth() && selected.getDate() === day && "bg-violet-600 text-white hover:bg-violet-500", isSameDay(new Date(), new Date(displayedMonth.getFullYear(), displayedMonth.getMonth(), day)) && "ring-1 ring-violet-400")} aria-label={`${MONTHS[displayedMonth.getMonth()]} ${day}, ${displayedMonth.getFullYear()}`}>{day}</button>)}</div>
      <div className="mt-3 flex items-center gap-2 border-t border-zinc-700 pt-3"><label className="text-xs text-zinc-400">Time</label><select aria-label="Hour" className="h-8 rounded-md border border-zinc-600 bg-[#0d0d11] px-2 text-sm text-white" value={selected?.getHours() ?? new Date().getHours()} onChange={(event) => changeTime("hour", event.target.value)}>{Array.from({ length: 24 }, (_, hour) => <option key={hour} value={hour}>{String(hour).padStart(2, "0")}</option>)}</select><span className="text-zinc-300">:</span><select aria-label="Minute" className="h-8 rounded-md border border-zinc-600 bg-[#0d0d11] px-2 text-sm text-white" value={selected?.getMinutes() ?? new Date().getMinutes()} onChange={(event) => changeTime("minute", event.target.value)}>{Array.from({ length: 60 }, (_, minute) => <option key={minute} value={minute}>{String(minute).padStart(2, "0")}</option>)}</select></div>
      <div className="mt-3 flex justify-end gap-2"><Button type="button" size="sm" variant="outline" onClick={() => onChange(null)}>Clear</Button><Button type="button" size="sm" className="bg-violet-600 text-white hover:bg-violet-500" onClick={() => setOpen(false)}>Done</Button></div>
    </dialog>, document.body)}
  </div>;
}
